import {
  Box,
  Flex,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  Input,
  Select,
  Button,
  IconButton,
  Text,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
} from '@chakra-ui/react';
import { useState, useMemo } from 'react';
import {
  AddIcon,
  DeleteIcon,
  EditIcon,
  InfoIcon,
  WarningIcon,
} from '@chakra-ui/icons';

import DateField from 'components/fields/DateField';
import MultiSelect from 'components/fields/MultiSelect';
import SelectSearch from 'components/fields/SelectSearch';
import {
  parseDateString,
  formatDateToString,
} from 'components/fields/auto/resolvers/resolverUtils';
import {
  CLAIM_STATUSES,
  FAULT_PERCENTAGES,
  createEmptyAutoClaim,
  createEmptyConviction,
  createEmptySuspension,
  createEmptyLapse,
  createEmptyFraudMisrepresentation,
} from '../../constants/driverOptions';
import { formatDate } from '../../constants';
import { useAnswerCodes } from '../../hooks/useAnswerCodes';
import { useValidationSchema } from '../../hooks/useValidationSchema';
import { ValidationEntityProvider } from '../../hooks/ValidationEntityContext';
import ClaimsList from '../../components/ClaimsList';
import ValidatedField from '../../components/ValidatedField';
import type { DriverIncidentsProps } from './types';
import type {
  AutoClaim,
  Conviction,
  Suspension,
  Lapse,
  FraudMisrepresentation,
  Discrepancy,
} from 'api/quote-marketplace';

type IncidentItem =
  | AutoClaim
  | Conviction
  | Suspension
  | Lapse
  | FraudMisrepresentation;

const DriverIncidents = ({
  driver,
  index,
  vehicles,
  discrepancies = [],
  onIncidentsChange,
}: DriverIncidentsProps) => {
  const incidents = driver.incidents;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalType, setModalType] = useState<string>('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [dateRangeErrors, setDateRangeErrors] = useState<
    Record<string, boolean>
  >({});
  const {
    getOptions,
    getOptionsWithTypes,
    getLabel,
    getSpeedingConvictionCodes,
    getConvictionOtherCodes,
    getSuspensionOtherCodes,
    getLapseOtherCodes,
    getCancelDeclineOtherCodes,
    getApplicantRelationCode,
    getAutomobileClaimTypeCode,
    getFraudPolicyStatusOptions,
  } = useAnswerCodes();
  const { isFieldRequired, validateField } = useValidationSchema();

  type IncidentType =
    | 'claim'
    | 'conviction'
    | 'suspension'
    | 'lapse'
    | 'fraud_misrepresentation';

  const INCIDENT_ITEM_PATHS = {
    claim: 'driver/claim',
    conviction: 'driver/conviction',
    suspension: 'driver/suspension',
    lapse: 'driver/lapse',
    fraud_misrepresentation: 'driver/fraud_misrepresentation',
  } as const;

  const isIncidentFieldRequired = (
    incidentType: IncidentType,
    fieldPath: string,
    item: Record<string, unknown>,
  ) => {
    const itemPath = INCIDENT_ITEM_PATHS[incidentType];
    return isFieldRequired(itemPath, fieldPath, item);
  };

  const getIncidentFieldError = (
    incidentType: IncidentType,
    fieldPath: string,
    item: Record<string, unknown>,
  ) => {
    const itemPath = INCIDENT_ITEM_PATHS[incidentType];
    return validateField(itemPath, fieldPath, item);
  };

  const INCIDENT_REQUIRED_FIELDS: Record<IncidentType, string[]> = {
    claim: ['date', 'cover_types'],
    conviction: ['date', 'conviction_code', 'speed_over_limit_kmh'],
    suspension: ['start_date', 'end_date', 'reason_code', 'reason_other'],
    lapse: ['reason_code', 'lapse_date', 'reinstatement_date', 'reason_other'],
    fraud_misrepresentation: ['policy_status_code', 'reason_code', 'date'],
  };

  const renderReportReferenceBox = (
    source: string,
    referenceData: { label: string; value: string | null | undefined }[],
  ) => {
    if (source !== 'mvr' && source !== 'dash') return null;

    const validData = referenceData.filter((d) => d.value);
    if (validData.length === 0) return null;

    const sourceLabel = source === 'mvr' ? 'MVR Report' : 'DASH Report';

    return (
      <Box
        bg="blue.50"
        borderLeft="4px solid"
        borderColor="blue.400"
        p={3}
        mb={4}
        borderRadius="md"
      >
        <Flex align="center" mb={2}>
          <InfoIcon color="blue.500" mr={2} />
          <Text fontSize="sm" fontWeight="600" color="blue.700">
            {sourceLabel} Reference
          </Text>
        </Flex>
        <VStack align="stretch" spacing={1}>
          {validData.map((d, idx) => (
            <Text key={idx} fontSize="sm" color="gray.700">
              <Text as="span" fontWeight="500">
                {d.label}:
              </Text>{' '}
              {d.value}
            </Text>
          ))}
        </VStack>
      </Box>
    );
  };

  const claimDiscrepancies = discrepancies.filter(
    (d) => d.type === 'new_claim' || d.type === 'at_fault_mismatch',
  );
  const convictionDiscrepancies = discrepancies.filter(
    (d) => d.type === 'new_conviction',
  );
  const suspensionDiscrepancies = discrepancies.filter(
    (d) => d.type === 'suspension_found',
  );
  const lapseDiscrepancies = discrepancies.filter(
    (d) => d.type === 'new_lapse',
  );

  const renderSectionDiscrepancies = (
    sectionDiscrepancies: Discrepancy[],
    getItemLabel: (item: Record<string, unknown>) => string,
  ) => {
    if (sectionDiscrepancies.length === 0) return null;

    return (
      <Box
        bg="orange.50"
        borderWidth="1px"
        borderColor="orange.200"
        borderRadius="md"
        p={2}
        mb={2}
      >
        <Flex align="center" mb={1}>
          <WarningIcon color="orange.500" boxSize={3} mr={1} />
          <Text fontSize="xs" fontWeight="600" color="orange.700">
            Report Findings
          </Text>
        </Flex>
        <VStack align="stretch" spacing={0}>
          {sectionDiscrepancies.map((d) => {
            const sourceLabel = d.source === 'mvr' ? 'MVR' : 'DASH';
            const reportItems = d.report_value as Array<
              Record<string, unknown>
            >;

            return Array.isArray(reportItems)
              ? reportItems.map((item, itemIdx) => (
                  <Text
                    key={`${d.index}-${itemIdx}`}
                    fontSize="xs"
                    color="gray.700"
                    py={0.5}
                  >
                    <Text as="span" fontWeight="600" color="orange.600">
                      {sourceLabel}:
                    </Text>{' '}
                    {getItemLabel(item)}
                  </Text>
                ))
              : null;
          })}
        </VStack>
      </Box>
    );
  };

  const isIncidentIncomplete = (
    incidentType: IncidentType,
    item: IncidentItem,
  ): boolean => {
    const fields = INCIDENT_REQUIRED_FIELDS[incidentType];
    return fields.some((field) =>
      getIncidentFieldError(
        incidentType,
        field,
        item as unknown as Record<string, unknown>,
      ),
    );
  };

  const applicantCode = getApplicantRelationCode();
  const isApplicant =
    driver.personal_info?.relation_to_applicant === applicantCode;

  const openAddModal = (type: string) => {
    setModalType(type);
    setEditingIndex(null);
    setDateRangeErrors({});
    switch (type) {
      case 'claims': {
        const newClaim = createEmptyAutoClaim(getNextId(incidents.claims));
        newClaim.type_code = getAutomobileClaimTypeCode() || null;
        setEditingItem(newClaim);
        break;
      }
      case 'convictions':
        setEditingItem(createEmptyConviction(getNextId(incidents.convictions)));
        break;
      case 'suspensions':
        setEditingItem(createEmptySuspension(getNextId(incidents.suspensions)));
        break;
      case 'lapses':
        setEditingItem(createEmptyLapse(getNextId(incidents.lapses)));
        break;
      case 'fraud_misrepresentation':
        setEditingItem(
          createEmptyFraudMisrepresentation(
            getNextId(incidents.fraud_misrepresentation),
          ),
        );
        break;
    }
    onOpen();
  };

  const openEditModal = (type: string, itemIndex: number, item: any) => {
    setModalType(type);
    setEditingIndex(itemIndex);
    setEditingItem({ ...item });
    setDateRangeErrors({});
    onOpen();
  };

  const getNextId = (items: any[]) => {
    return items.length > 0 ? Math.max(...items.map((i) => i.id)) + 1 : 1;
  };

  const handleDateOutOfRange = (field: string) => (isOutOfRange: boolean) => {
    setDateRangeErrors((prev) => ({ ...prev, [field]: isOutOfRange }));
  };

  const hasDateRangeErrors = useMemo(
    () => Object.values(dateRangeErrors).some(Boolean),
    [dateRangeErrors],
  );

  const isLapseOutOfSequence =
    modalType === 'lapses' &&
    !!editingItem?.lapse_date &&
    !!editingItem?.reinstatement_date &&
    editingItem.lapse_date > editingItem.reinstatement_date;

  const handleSave = () => {
    const currentItems = incidents[
      modalType as keyof typeof incidents
    ] as any[];
    let updated: any[];

    if (editingIndex !== null) {
      updated = [...currentItems];
      updated[editingIndex] = editingItem;
    } else {
      updated = [...currentItems, editingItem];
    }

    onIncidentsChange(index, modalType, updated);
    onClose();
  };

  const handleDelete = (type: string, itemIndex: number) => {
    const currentItems = incidents[type as keyof typeof incidents] as any[];
    const updated = currentItems.filter((_, i) => i !== itemIndex);
    onIncidentsChange(index, type, updated);
  };

  const renderModalContent = () => {
    if (!editingItem) return null;

    switch (modalType) {
      case 'claims': {
        const isDateRequired = isIncidentFieldRequired(
          'claim',
          'date',
          editingItem,
        );
        const isCoverTypesRequired = isIncidentFieldRequired(
          'claim',
          'cover_types',
          editingItem,
        );
        const coverTypeOptions = getOptions('claim_cover_type').filter(
          (opt) => opt.value,
        );
        const coverTypeItems = coverTypeOptions.map((opt, idx) => ({
          id: idx,
          name: opt.label,
        }));
        const selectedCoverTypeIds = (
          (editingItem.cover_types as string[] | undefined) ?? []
        )
          .map((code) => coverTypeOptions.findIndex((o) => o.value === code))
          .filter((id) => id !== -1);

        const claimCauses = getOptionsWithTypes('claim_cause');
        const automobileClaimTypeCode = getAutomobileClaimTypeCode();
        const filteredCauses = automobileClaimTypeCode
          ? claimCauses.filter(
              (cause) =>
                !cause.available_for_types?.length ||
                cause.available_for_types.includes(automobileClaimTypeCode),
            )
          : claimCauses;

        return (
          <>
            {renderReportReferenceBox(editingItem.source, [
              { label: 'Claim Number', value: editingItem.dash_claim_number },
              { label: 'Loss Type', value: editingItem.dash_loss_description },
              {
                label: 'Coverage',
                value: editingItem.dash_coverage_description,
              },
            ])}
            <Grid
              templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
              gap={4}
            >
              <GridItem>
                <DateField
                  label="Date"
                  date={parseDateString(editingItem.date)}
                  onChange={(date: Date | null) =>
                    setEditingItem({
                      ...editingItem,
                      date: formatDateToString(date),
                    })
                  }
                  maxDate={new Date()}
                  onOutOfRange={handleDateOutOfRange('claim_date')}
                  isInvalid={dateRangeErrors['claim_date']}
                  size="sm"
                  required={isDateRequired}
                />
                {dateRangeErrors['claim_date'] && (
                  <Text fontSize="xs" color="red.600" mt={1}>
                    Date cannot be in the future
                  </Text>
                )}
              </GridItem>
              <GridItem colSpan={{ base: 1, md: 2 }}>
                <FormControl
                  isRequired={isCoverTypesRequired}
                  isInvalid={
                    !!getIncidentFieldError('claim', 'cover_types', editingItem)
                  }
                >
                  <FormLabel fontSize="sm" whiteSpace="nowrap">
                    Cover Types
                  </FormLabel>
                  <MultiSelect
                    selectedIds={selectedCoverTypeIds}
                    availableItems={coverTypeItems}
                    isLoading={false}
                    onChange={(ids) =>
                      setEditingItem({
                        ...editingItem,
                        cover_types: ids.map(
                          (id) => coverTypeOptions[id].value,
                        ),
                      })
                    }
                    label="Select cover types"
                    itemLabelSingular="cover type"
                    itemLabelPlural="cover types"
                    colorScheme="brand"
                  />
                  {editingItem.source === 'dash' &&
                    editingItem.dash_coverage && (
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        DASH coverage: {editingItem.dash_coverage}
                      </Text>
                    )}
                </FormControl>

                <ValidatedField
                  itemPath="driver/claim"
                  fieldPath="cause_code"
                  label="Cause"
                  entity={editingItem}
                >
                  <SelectSearch
                    options={filteredCauses}
                    value={editingItem.cause_code || ''}
                    onChange={(value) =>
                      setEditingItem({
                        ...editingItem,
                        cause_code: value || null,
                      })
                    }
                    placeholder="Select cause..."
                  />
                </ValidatedField>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm" whiteSpace="nowrap">
                    Vehicle
                  </FormLabel>
                  <Select
                    variant="main"
                    size="sm"
                    value={
                      editingItem.item_id ??
                      (editingItem.item_description ? 'dash' : '')
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === 'dash') {
                        return; // Keep current DASH vehicle
                      }
                      const vehicleId = value ? Number(value) : null;
                      const vehicle = vehicles.find((v) => v.id === vehicleId);
                      setEditingItem({
                        ...editingItem,
                        item_id: vehicleId,
                        item_description: vehicle
                          ? `${vehicle.vehicle_model_year || ''} ${vehicle.make} ${vehicle.model}`.trim()
                          : '',
                      });
                    }}
                  >
                    <option value="">Select vehicle...</option>
                    {!editingItem.item_id && editingItem.item_description && (
                      <option value="dash">
                        {editingItem.item_description} (from DASH)
                      </option>
                    )}
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.vehicle_model_year || ''} {v.make} {v.model}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm" whiteSpace="nowrap">
                    Status
                  </FormLabel>
                  <Select
                    variant="main"
                    size="sm"
                    value={editingItem.status}
                    onChange={(e) =>
                      setEditingItem({ ...editingItem, status: e.target.value })
                    }
                  >
                    {CLAIM_STATUSES.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm" whiteSpace="nowrap">
                    Fault %
                  </FormLabel>
                  <Select
                    variant="main"
                    size="sm"
                    value={editingItem.fault_percentage ?? 0}
                    onChange={(e) =>
                      setEditingItem({
                        ...editingItem,
                        fault_percentage: Number(e.target.value),
                      })
                    }
                  >
                    {FAULT_PERCENTAGES.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm" whiteSpace="nowrap">
                    Collision Payout
                  </FormLabel>
                  <Input
                    variant="main"
                    size="sm"
                    type="number"
                    value={editingItem.payout_collision || ''}
                    onChange={(e) =>
                      setEditingItem({
                        ...editingItem,
                        payout_collision: e.target.value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm" whiteSpace="nowrap">
                    AB Payout
                  </FormLabel>
                  <Input
                    variant="main"
                    size="sm"
                    type="number"
                    value={editingItem.payout_accident_benefits || ''}
                    onChange={(e) =>
                      setEditingItem({
                        ...editingItem,
                        payout_accident_benefits: e.target.value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm" whiteSpace="nowrap">
                    Liability BI Payout
                  </FormLabel>
                  <Input
                    variant="main"
                    size="sm"
                    type="number"
                    value={editingItem.payout_liability_bodily_injury || ''}
                    onChange={(e) =>
                      setEditingItem({
                        ...editingItem,
                        payout_liability_bodily_injury: e.target.value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm" whiteSpace="nowrap">
                    Liability PD Payout
                  </FormLabel>
                  <Input
                    variant="main"
                    size="sm"
                    type="number"
                    value={editingItem.payout_liability_property_damage || ''}
                    onChange={(e) =>
                      setEditingItem({
                        ...editingItem,
                        payout_liability_property_damage: e.target.value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm" whiteSpace="nowrap">
                    Other PD Payout
                  </FormLabel>
                  <Input
                    variant="main"
                    size="sm"
                    type="number"
                    value={editingItem.payout_other_pd || ''}
                    onChange={(e) =>
                      setEditingItem({
                        ...editingItem,
                        payout_other_pd: e.target.value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                  />
                </FormControl>
              </GridItem>
            </Grid>
          </>
        );
      }

      case 'convictions': {
        const speedingCodes = getSpeedingConvictionCodes();
        const otherCodes = getConvictionOtherCodes();
        const showSpeedOverLimit = speedingCodes.includes(
          editingItem.conviction_code || '',
        );
        const showOtherField = otherCodes.includes(
          editingItem.conviction_code || '',
        );
        const isDateRequired = isIncidentFieldRequired(
          'conviction',
          'date',
          editingItem,
        );
        const isCodeRequired = isIncidentFieldRequired(
          'conviction',
          'conviction_code',
          editingItem,
        );
        const isOtherRequired = isIncidentFieldRequired(
          'conviction',
          'conviction_code_other',
          editingItem,
        );
        const isSpeedRequired = isIncidentFieldRequired(
          'conviction',
          'speed_over_limit_kmh',
          editingItem,
        );
        return (
          <>
            {renderReportReferenceBox(editingItem.source, [
              { label: 'Code', value: editingItem.mvr_code },
              { label: 'Description', value: editingItem.mvr_description },
              { label: 'Offence Date', value: editingItem.mvr_offence_date },
            ])}
            <Grid
              templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
              gap={4}
            >
              <GridItem>
                <DateField
                  label="Date"
                  date={parseDateString(editingItem.date)}
                  onChange={(date: Date | null) =>
                    setEditingItem({
                      ...editingItem,
                      date: formatDateToString(date),
                    })
                  }
                  maxDate={new Date()}
                  onOutOfRange={handleDateOutOfRange('conviction_date')}
                  isInvalid={dateRangeErrors['conviction_date']}
                  size="sm"
                  required={isDateRequired}
                />
                {dateRangeErrors['conviction_date'] && (
                  <Text fontSize="xs" color="red.600" mt={1}>
                    Date cannot be in the future
                  </Text>
                )}
              </GridItem>
              <GridItem colSpan={{ base: 1, md: 2 }}>
                <FormControl
                  isRequired={isCodeRequired}
                  isInvalid={
                    !!getIncidentFieldError(
                      'conviction',
                      'conviction_code',
                      editingItem,
                    )
                  }
                >
                  <FormLabel fontSize="sm" whiteSpace="nowrap">
                    Conviction Type
                  </FormLabel>
                  <SelectSearch
                    options={getOptions('conviction_code')}
                    value={editingItem.conviction_code || ''}
                    onChange={(value) => {
                      const code = String(value || '');
                      setEditingItem({
                        ...editingItem,
                        conviction_code: code || null,
                        conviction_code_other: otherCodes.includes(code)
                          ? editingItem.conviction_code_other
                          : null,
                        speed_over_limit_kmh: speedingCodes.includes(code)
                          ? editingItem.speed_over_limit_kmh
                          : null,
                      });
                    }}
                    isInvalid={
                      !!getIncidentFieldError(
                        'conviction',
                        'conviction_code',
                        editingItem,
                      )
                    }
                    placeholder="Search conviction types..."
                  />
                </FormControl>
              </GridItem>
              {showOtherField && (
                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <FormControl
                    isRequired={isOtherRequired}
                    isInvalid={
                      !!getIncidentFieldError(
                        'conviction',
                        'conviction_code_other',
                        editingItem,
                      )
                    }
                  >
                    <FormLabel fontSize="sm" whiteSpace="nowrap">
                      Please Specify
                    </FormLabel>
                    <Input
                      variant="main"
                      size="sm"
                      value={editingItem.conviction_code_other || ''}
                      onChange={(e) =>
                        setEditingItem({
                          ...editingItem,
                          conviction_code_other: e.target.value || null,
                        })
                      }
                      placeholder="Describe the conviction..."
                    />
                  </FormControl>
                </GridItem>
              )}
              {showSpeedOverLimit && (
                <GridItem>
                  <FormControl
                    isRequired={isSpeedRequired}
                    isInvalid={
                      !!getIncidentFieldError(
                        'conviction',
                        'speed_over_limit_kmh',
                        editingItem,
                      )
                    }
                  >
                    <FormLabel fontSize="sm" whiteSpace="nowrap">
                      Speed Over Limit (km/h)
                    </FormLabel>
                    <Input
                      variant="main"
                      size="sm"
                      type="number"
                      value={editingItem.speed_over_limit_kmh || ''}
                      onChange={(e) =>
                        setEditingItem({
                          ...editingItem,
                          speed_over_limit_kmh: e.target.value
                            ? Number(e.target.value)
                            : null,
                        })
                      }
                      placeholder="km/h"
                    />
                  </FormControl>
                </GridItem>
              )}
            </Grid>
          </>
        );
      }

      case 'suspensions': {
        const suspensionOtherCodes = getSuspensionOtherCodes();
        const showReasonOther = suspensionOtherCodes.includes(
          editingItem.reason_code || '',
        );
        const isStartRequired = isIncidentFieldRequired(
          'suspension',
          'start_date',
          editingItem,
        );
        const isEndRequired = isIncidentFieldRequired(
          'suspension',
          'end_date',
          editingItem,
        );
        const isReasonRequired = isIncidentFieldRequired(
          'suspension',
          'reason_code',
          editingItem,
        );
        const isOtherRequired = isIncidentFieldRequired(
          'suspension',
          'reason_other',
          editingItem,
        );

        const getSuspensionDuration = (): string | null => {
          if (!editingItem.start_date || !editingItem.end_date) return null;
          const start = new Date(editingItem.start_date);
          const end = new Date(editingItem.end_date);
          if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
          if (end < start) return null;

          const diffMs = end.getTime() - start.getTime();
          const totalDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
          const years = Math.floor(totalDays / 365);
          const months = Math.floor((totalDays % 365) / 30);
          const days = (totalDays % 365) % 30;

          const parts: string[] = [];
          if (years > 0) parts.push(`${years}y`);
          if (months > 0) parts.push(`${months}m`);
          if (days > 0 || parts.length === 0) parts.push(`${days}d`);

          return parts.join(' ');
        };

        const suspensionDuration = getSuspensionDuration();

        return (
          <>
            {renderReportReferenceBox(editingItem.source, [
              { label: 'Description', value: editingItem.mvr_description },
            ])}
            <Grid
              templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
              gap={4}
            >
              <GridItem>
                <DateField
                  label="Start Date"
                  date={parseDateString(editingItem.start_date)}
                  onChange={(date: Date | null) =>
                    setEditingItem({
                      ...editingItem,
                      start_date: formatDateToString(date),
                    })
                  }
                  size="sm"
                  required={isStartRequired}
                />
              </GridItem>
              <GridItem>
                <DateField
                  label="End Date"
                  date={parseDateString(editingItem.end_date)}
                  onChange={(date: Date | null) =>
                    setEditingItem({
                      ...editingItem,
                      end_date: formatDateToString(date),
                    })
                  }
                  size="sm"
                  required={isEndRequired}
                />
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm" whiteSpace="nowrap">
                    Duration
                  </FormLabel>
                  <Input
                    variant="main"
                    size="sm"
                    value={suspensionDuration || '—'}
                    isReadOnly
                    bg="gray.50"
                    color={suspensionDuration ? 'inherit' : 'gray.400'}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={{ base: 1, md: 3 }}>
                <FormControl
                  isRequired={isReasonRequired}
                  isInvalid={
                    !!getIncidentFieldError(
                      'suspension',
                      'reason_code',
                      editingItem,
                    )
                  }
                >
                  <FormLabel fontSize="sm" whiteSpace="nowrap">
                    Reason
                  </FormLabel>
                  <Select
                    variant="main"
                    size="sm"
                    value={editingItem.reason_code || ''}
                    onChange={(e) =>
                      setEditingItem({
                        ...editingItem,
                        reason_code: e.target.value || null,
                        reason: e.target.value
                          ? getLabel(e.target.value, 'suspension_reason')
                          : '',
                        reason_other: suspensionOtherCodes.includes(
                          e.target.value,
                        )
                          ? editingItem.reason_other
                          : null,
                      })
                    }
                  >
                    {getOptions('suspension_reason').map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              {showReasonOther && (
                <GridItem colSpan={{ base: 1, md: 3 }}>
                  <FormControl
                    isRequired={isOtherRequired}
                    isInvalid={
                      !!getIncidentFieldError(
                        'suspension',
                        'reason_other',
                        editingItem,
                      )
                    }
                  >
                    <FormLabel fontSize="sm" whiteSpace="nowrap">
                      Other Reason
                    </FormLabel>
                    <Input
                      variant="main"
                      size="sm"
                      value={editingItem.reason_other || ''}
                      onChange={(e) =>
                        setEditingItem({
                          ...editingItem,
                          reason_other: e.target.value || null,
                        })
                      }
                      placeholder="Specify reason..."
                    />
                  </FormControl>
                </GridItem>
              )}
            </Grid>
          </>
        );
      }

      case 'lapses': {
        const lapseReasonField = isApplicant
          ? 'lapse_reason_applicant'
          : 'lapse_reason_non_applicant';
        const lapseOtherCodes = getLapseOtherCodes(isApplicant);
        const showLapseOther = lapseOtherCodes.includes(
          editingItem.reason_code || '',
        );
        const isReasonRequired = isIncidentFieldRequired(
          'lapse',
          'reason_code',
          editingItem,
        );
        const isOtherRequired = isIncidentFieldRequired(
          'lapse',
          'reason_other',
          editingItem,
        );
        const isLapseDateRequired = isIncidentFieldRequired(
          'lapse',
          'lapse_date',
          editingItem,
        );
        const isReinstatementDateRequired = isIncidentFieldRequired(
          'lapse',
          'reinstatement_date',
          editingItem,
        );
        return (
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
            <GridItem colSpan={{ base: 1, md: 2 }}>
              <FormControl
                isRequired={isReasonRequired}
                isInvalid={
                  !!getIncidentFieldError('lapse', 'reason_code', editingItem)
                }
              >
                <FormLabel fontSize="sm" whiteSpace="nowrap">
                  Reason
                </FormLabel>
                <Select
                  variant="main"
                  size="sm"
                  value={editingItem.reason_code || ''}
                  onChange={(e) =>
                    setEditingItem({
                      ...editingItem,
                      reason_code: e.target.value || null,
                      reason: e.target.value
                        ? getLabel(e.target.value, lapseReasonField)
                        : '',
                      reason_other: lapseOtherCodes.includes(e.target.value)
                        ? editingItem.reason_other
                        : null,
                    })
                  }
                >
                  {getOptions(lapseReasonField).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>
            {showLapseOther && (
              <GridItem colSpan={{ base: 1, md: 2 }}>
                <FormControl
                  isRequired={isOtherRequired}
                  isInvalid={
                    !!getIncidentFieldError(
                      'lapse',
                      'reason_other',
                      editingItem,
                    )
                  }
                >
                  <FormLabel fontSize="sm" whiteSpace="nowrap">
                    Other Reason
                  </FormLabel>
                  <Input
                    variant="main"
                    size="sm"
                    value={editingItem.reason_other || ''}
                    onChange={(e) =>
                      setEditingItem({
                        ...editingItem,
                        reason_other: e.target.value || null,
                      })
                    }
                    placeholder="Specify reason..."
                  />
                </FormControl>
              </GridItem>
            )}
            <GridItem>
              <DateField
                label="Lapse Date"
                date={parseDateString(editingItem.lapse_date)}
                onChange={(date: Date | null) => {
                  const lapseDate = formatDateToString(date);
                  setEditingItem((prev: any) => {
                    let durationMonths = null;
                    if (lapseDate && prev.reinstatement_date) {
                      const start = new Date(lapseDate);
                      const end = new Date(prev.reinstatement_date);
                      durationMonths = Math.max(
                        0,
                        Math.round(
                          (end.getTime() - start.getTime()) /
                            (1000 * 60 * 60 * 24 * 30),
                        ),
                      );
                    }
                    return {
                      ...prev,
                      lapse_date: lapseDate,
                      duration_months: durationMonths,
                    };
                  });
                }}
                size="sm"
                required={isLapseDateRequired}
                maxDate={new Date()}
                onOutOfRange={handleDateOutOfRange('lapse_date')}
                isInvalid={
                  dateRangeErrors['lapse_date'] || isLapseOutOfSequence
                }
              />
              {dateRangeErrors['lapse_date'] && (
                <Text fontSize="xs" color="red.600" mt={1}>
                  Date cannot be in the future
                </Text>
              )}
            </GridItem>
            <GridItem>
              <DateField
                label="Reinstatement Date"
                date={parseDateString(editingItem.reinstatement_date)}
                onChange={(date: Date | null) => {
                  const reinstatementDate = formatDateToString(date);
                  setEditingItem((prev: any) => {
                    let durationMonths = null;
                    if (prev.lapse_date && reinstatementDate) {
                      const start = new Date(prev.lapse_date);
                      const end = new Date(reinstatementDate);
                      durationMonths = Math.max(
                        0,
                        Math.round(
                          (end.getTime() - start.getTime()) /
                            (1000 * 60 * 60 * 24 * 30),
                        ),
                      );
                    }
                    return {
                      ...prev,
                      reinstatement_date: reinstatementDate,
                      duration_months: durationMonths,
                    };
                  });
                }}
                size="sm"
                required={isReinstatementDateRequired}
                maxDate={new Date()}
                onOutOfRange={handleDateOutOfRange('reinstatement_date')}
                isInvalid={
                  dateRangeErrors['reinstatement_date'] || isLapseOutOfSequence
                }
              />
              {dateRangeErrors['reinstatement_date'] && (
                <Text fontSize="xs" color="red.600" mt={1}>
                  Date cannot be in the future
                </Text>
              )}
              {isLapseOutOfSequence &&
                !dateRangeErrors['reinstatement_date'] && (
                  <Text fontSize="xs" color="red.600" mt={1}>
                    Reinstatement date cannot be before the lapse date
                  </Text>
                )}
            </GridItem>
            {editingItem.duration_months != null && (
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm" whiteSpace="nowrap">
                    Duration (months)
                  </FormLabel>
                  <Input
                    variant="main"
                    size="sm"
                    value={editingItem.duration_months}
                    isReadOnly
                    bg="gray.50"
                  />
                </FormControl>
              </GridItem>
            )}
          </Grid>
        );
      }

      case 'fraud_misrepresentation': {
        const fraudReasonField = isApplicant
          ? 'cancel_decline_reason_applicant'
          : 'cancel_decline_reason_non_applicant';
        const reasonOptions = getOptions(fraudReasonField);
        const policyStatusOptions = getFraudPolicyStatusOptions(isApplicant);
        const fraudOtherCodes = getCancelDeclineOtherCodes(isApplicant);
        const showFraudOther = fraudOtherCodes.includes(
          editingItem.reason_code || '',
        );
        const isDateRequired = isIncidentFieldRequired(
          'fraud_misrepresentation',
          'date',
          editingItem,
        );
        const isOtherRequired = isIncidentFieldRequired(
          'fraud_misrepresentation',
          'reason_other',
          editingItem,
        );
        return (
          <ValidationEntityProvider entity={editingItem}>
            <Grid
              templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
              gap={4}
            >
              <GridItem>
                <ValidatedField
                  itemPath="driver/fraud_misrepresentation"
                  fieldPath="policy_status_code"
                  label="Policy Status"
                >
                  <Select
                    variant="main"
                    size="sm"
                    value={editingItem.policy_status_code || ''}
                    onChange={(e) =>
                      setEditingItem({
                        ...editingItem,
                        policy_status_code: e.target.value || null,
                      })
                    }
                  >
                    {policyStatusOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </ValidatedField>
              </GridItem>
              <GridItem>
                <ValidatedField
                  itemPath="driver/fraud_misrepresentation"
                  fieldPath="reason_code"
                  label="Cause"
                >
                  <Select
                    variant="main"
                    size="sm"
                    value={editingItem.reason_code || ''}
                    onChange={(e) =>
                      setEditingItem({
                        ...editingItem,
                        reason_code: e.target.value || null,
                        reason_other: fraudOtherCodes.includes(e.target.value)
                          ? editingItem.reason_other
                          : null,
                      })
                    }
                  >
                    {reasonOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </ValidatedField>
              </GridItem>
              {showFraudOther && (
                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <FormControl
                    isRequired={isOtherRequired}
                    isInvalid={
                      !!getIncidentFieldError(
                        'fraud_misrepresentation',
                        'reason_other',
                        editingItem,
                      )
                    }
                  >
                    <FormLabel fontSize="sm" whiteSpace="nowrap">
                      Other Cause
                    </FormLabel>
                    <Input
                      variant="main"
                      size="sm"
                      value={editingItem.reason_other || ''}
                      onChange={(e) =>
                        setEditingItem({
                          ...editingItem,
                          reason_other: e.target.value || null,
                        })
                      }
                      placeholder="Specify cause..."
                    />
                  </FormControl>
                </GridItem>
              )}
              <GridItem>
                <DateField
                  label="Date"
                  date={parseDateString(editingItem.date)}
                  onChange={(date: Date | null) =>
                    setEditingItem({
                      ...editingItem,
                      date: formatDateToString(date),
                    })
                  }
                  size="sm"
                  required={isDateRequired}
                  maxDate={new Date()}
                  onOutOfRange={handleDateOutOfRange('fraud_date')}
                  isInvalid={dateRangeErrors['fraud_date']}
                />
                {dateRangeErrors['fraud_date'] && (
                  <Text fontSize="xs" color="red.600" mt={1}>
                    Date cannot be in the future
                  </Text>
                )}
              </GridItem>
            </Grid>
          </ValidationEntityProvider>
        );
      }

      default:
        return null;
    }
  };

  const getModalTitle = () => {
    const action = editingIndex !== null ? 'Edit' : 'Add';
    switch (modalType) {
      case 'claims':
        return `${action} Claim`;
      case 'convictions':
        return `${action} Conviction`;
      case 'suspensions':
        return `${action} Suspension`;
      case 'lapses':
        return `${action} Lapse`;
      case 'fraud_misrepresentation':
        return `${action} Fraud/Misrepresentation`;
      default:
        return action;
    }
  };

  const formatAutoClaimDisplay = (claim: AutoClaim) => {
    const date = claim.date || 'No date';
    const fault = (claim.fault_percentage ?? 0) > 0 ? 'AF' : 'NAF';
    const total =
      (claim.payout_collision || 0) +
      (claim.payout_accident_benefits || 0) +
      (claim.payout_liability_bodily_injury || 0) +
      (claim.payout_liability_property_damage || 0) +
      (claim.payout_other_pd || 0);
    const amount = total > 0 ? ` - $${total}` : '';
    return `${date} - ${fault}${amount}`;
  };

  const renderClaimsItems = () => (
    <ClaimsList
      claims={incidents.claims}
      formatDisplay={formatAutoClaimDisplay}
      onEdit={(idx, claim) => openEditModal('claims', idx, claim)}
      onDelete={(idx) => handleDelete('claims', idx)}
      isIncomplete={(claim) => isIncidentIncomplete('claim', claim)}
      isUpdating={false}
    />
  );

  const renderConvictionsItems = () => (
    <VStack spacing={1} align="stretch" pl={2}>
      {incidents.convictions.map((conviction, idx) => {
        const isIncomplete = isIncidentIncomplete('conviction', conviction);
        return (
          <Box
            key={conviction.id}
            position="relative"
            py={1}
            role="group"
            _hover={{ bg: 'gray.50' }}
            borderRadius="sm"
          >
            <Flex align="center" gap={1}>
              {isIncomplete && (
                <WarningIcon color="orange.500" boxSize={3} flexShrink={0} />
              )}
              <Text
                fontSize="xs"
                color={isIncomplete ? 'orange.700' : 'gray.700'}
              >
                • {conviction.date || 'No date'} -{' '}
                {conviction.conviction_code
                  ? getLabel(conviction.conviction_code, 'conviction_code')
                  : 'No type'}
                {conviction.speed_over_limit_kmh
                  ? ` - ${conviction.speed_over_limit_kmh}km/h over`
                  : ''}
              </Text>
            </Flex>
            <Flex
              position="absolute"
              top={0}
              right={0}
              gap={0}
              opacity={0}
              _groupHover={{ opacity: 1 }}
              bg="gray.50"
              borderRadius="sm"
            >
              <IconButton
                aria-label="Edit"
                icon={<EditIcon />}
                size="xs"
                variant="ghost"
                onClick={() => openEditModal('convictions', idx, conviction)}
                minW="auto"
                h="auto"
                p={1}
              />
              <IconButton
                aria-label="Delete"
                icon={<DeleteIcon />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={() => handleDelete('convictions', idx)}
                isDisabled={false}
                minW="auto"
                h="auto"
                p={1}
              />
            </Flex>
          </Box>
        );
      })}
    </VStack>
  );

  const renderSuspensionsItems = () => (
    <VStack spacing={1} align="stretch" pl={2}>
      {incidents.suspensions.map((suspension, idx) => {
        const isIncomplete = isIncidentIncomplete('suspension', suspension);
        return (
          <Box
            key={suspension.id}
            position="relative"
            py={1}
            role="group"
            _hover={{ bg: 'gray.50' }}
            borderRadius="sm"
          >
            <Flex align="center" gap={1}>
              {isIncomplete && (
                <WarningIcon color="orange.500" boxSize={3} flexShrink={0} />
              )}
              <Text
                fontSize="xs"
                color={isIncomplete ? 'orange.700' : 'gray.700'}
              >
                • {suspension.start_date || 'No date'} -{' '}
                {suspension.end_date || 'ongoing'} -{' '}
                {suspension.reason_code
                  ? getLabel(suspension.reason_code, 'suspension_reason')
                  : suspension.reason || 'No reason'}
              </Text>
            </Flex>
            <Flex
              position="absolute"
              top={0}
              right={0}
              gap={0}
              opacity={0}
              _groupHover={{ opacity: 1 }}
              bg="gray.50"
              borderRadius="sm"
            >
              <IconButton
                aria-label="Edit"
                icon={<EditIcon />}
                size="xs"
                variant="ghost"
                onClick={() => openEditModal('suspensions', idx, suspension)}
                minW="auto"
                h="auto"
                p={1}
              />
              <IconButton
                aria-label="Delete"
                icon={<DeleteIcon />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={() => handleDelete('suspensions', idx)}
                isDisabled={false}
                minW="auto"
                h="auto"
                p={1}
              />
            </Flex>
          </Box>
        );
      })}
    </VStack>
  );

  const renderLapsesItems = () => {
    const lapseReasonField = isApplicant
      ? 'lapse_reason_applicant'
      : 'lapse_reason_non_applicant';
    return (
      <VStack spacing={1} align="stretch" pl={2}>
        {incidents.lapses.map((lapse, idx) => {
          const isIncomplete = isIncidentIncomplete('lapse', lapse);
          return (
            <Box
              key={lapse.id}
              position="relative"
              py={1}
              role="group"
              _hover={{ bg: 'gray.50' }}
              borderRadius="sm"
            >
              <Flex align="center" gap={1}>
                {isIncomplete && (
                  <WarningIcon color="orange.500" boxSize={3} flexShrink={0} />
                )}
                <Text
                  fontSize="xs"
                  color={isIncomplete ? 'orange.700' : 'gray.700'}
                >
                  • {lapse.lapse_date || 'No date'} -{' '}
                  {lapse.reinstatement_date || 'ongoing'}
                  {lapse.duration_months
                    ? ` (${lapse.duration_months} months)`
                    : ''}{' '}
                  -{' '}
                  {lapse.reason_code
                    ? getLabel(lapse.reason_code, lapseReasonField)
                    : lapse.reason || 'No reason'}
                </Text>
              </Flex>
              <Flex
                position="absolute"
                top={0}
                right={0}
                gap={0}
                opacity={0}
                _groupHover={{ opacity: 1 }}
                bg="gray.50"
                borderRadius="sm"
              >
                <IconButton
                  aria-label="Edit"
                  icon={<EditIcon />}
                  size="xs"
                  variant="ghost"
                  onClick={() => openEditModal('lapses', idx, lapse)}
                  minW="auto"
                  h="auto"
                  p={1}
                />
                <IconButton
                  aria-label="Delete"
                  icon={<DeleteIcon />}
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => handleDelete('lapses', idx)}
                  isDisabled={false}
                  minW="auto"
                  h="auto"
                  p={1}
                />
              </Flex>
            </Box>
          );
        })}
      </VStack>
    );
  };

  const renderFraudItems = () => {
    const fraudReasonField = isApplicant
      ? 'cancel_decline_reason_applicant'
      : 'cancel_decline_reason_non_applicant';
    return (
      <VStack spacing={1} align="stretch" pl={2}>
        {incidents.fraud_misrepresentation.map((fraud, idx) => {
          const isIncomplete = isIncidentIncomplete(
            'fraud_misrepresentation',
            fraud,
          );
          return (
            <Box
              key={fraud.id}
              position="relative"
              py={1}
              role="group"
              _hover={{ bg: 'gray.50' }}
              borderRadius="sm"
            >
              <Flex align="center" gap={1}>
                {isIncomplete && (
                  <WarningIcon color="orange.500" boxSize={3} flexShrink={0} />
                )}
                <Text
                  fontSize="xs"
                  color={isIncomplete ? 'orange.700' : 'gray.700'}
                >
                  • {fraud.date || 'No date'} -{' '}
                  {fraud.reason_code
                    ? getLabel(fraud.reason_code, fraudReasonField)
                    : 'No type'}
                </Text>
              </Flex>
              <Flex
                position="absolute"
                top={0}
                right={0}
                gap={0}
                opacity={0}
                _groupHover={{ opacity: 1 }}
                bg="gray.50"
                borderRadius="sm"
              >
                <IconButton
                  aria-label="Edit"
                  icon={<EditIcon />}
                  size="xs"
                  variant="ghost"
                  onClick={() =>
                    openEditModal('fraud_misrepresentation', idx, fraud)
                  }
                  minW="auto"
                  h="auto"
                  p={1}
                />
                <IconButton
                  aria-label="Delete"
                  icon={<DeleteIcon />}
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => handleDelete('fraud_misrepresentation', idx)}
                  isDisabled={false}
                  minW="auto"
                  h="auto"
                  p={1}
                />
              </Flex>
            </Box>
          );
        })}
      </VStack>
    );
  };

  const incidentSections = [
    {
      key: 'claims',
      title: 'Claims',
      color: 'brand',
      items: incidents.claims,
      renderItems: renderClaimsItems,
      renderDiscrepancies: () =>
        renderSectionDiscrepancies(
          claimDiscrepancies,
          (item) =>
            `${formatDate(item.date as string)} - ${item.description || 'Claim'}${(item.fault_percentage ?? 0) > 0 ? ' (At-fault)' : ''}`,
        ),
    },
    {
      key: 'convictions',
      title: 'Convictions',
      color: 'brand',
      items: incidents.convictions,
      renderItems: renderConvictionsItems,
      renderDiscrepancies: () =>
        renderSectionDiscrepancies(
          convictionDiscrepancies,
          (item) =>
            `${formatDate(item.date as string)} - ${item.description || item.code || 'Conviction'}`,
        ),
    },
    {
      key: 'suspensions',
      title: 'Suspensions',
      color: 'brand',
      items: incidents.suspensions,
      renderItems: renderSuspensionsItems,
      renderDiscrepancies: () =>
        renderSectionDiscrepancies(
          suspensionDiscrepancies,
          (item) =>
            `${formatDate(item.start_date as string)} to ${formatDate(item.expiry_date as string)} - ${item.description || 'Suspension'}`,
        ),
    },
    {
      key: 'lapses',
      title: 'Insurance Lapse',
      color: 'brand',
      items: incidents.lapses,
      renderItems: renderLapsesItems,
      renderDiscrepancies: () =>
        renderSectionDiscrepancies(
          lapseDiscrepancies,
          (item) =>
            `${formatDate(item.lapse_date as string)} to ${
              item.reinstatement_date
                ? formatDate(item.reinstatement_date as string)
                : 'ongoing'
            }${
              item.duration_months != null
                ? ` (${item.duration_months} months)`
                : ''
            }`,
        ),
    },
    {
      key: 'fraud_misrepresentation',
      title: 'Fraud / Misrepresentation',
      color: 'brand',
      items: incidents.fraud_misrepresentation,
      renderItems: renderFraudItems,
      renderDiscrepancies: (): null => null,
    },
  ];

  return (
    <>
      <VStack spacing={4} align="stretch">
        {incidentSections.map((section) => (
          <Box key={section.key}>
            <Button
              size="sm"
              variant="solid"
              colorScheme={section.color}
              borderRadius="full"
              leftIcon={<AddIcon boxSize={3} />}
              onClick={() => openAddModal(section.key)}
              isDisabled={false}
              mb={2}
              w="100%"
              justifyContent="center"
            >
              {section.title}
            </Button>
            {section.renderDiscrepancies()}
            {section.items.length > 0 && section.renderItems()}
          </Box>
        ))}
      </VStack>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{getModalTitle()}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>{renderModalContent()}</ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleSave}
              isDisabled={hasDateRangeErrors || isLapseOutOfSequence}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default DriverIncidents;
